import { API_BASE_URL } from "../api";
import { readEvents } from "./chat";

export const SANDBOX_URL = `${API_BASE_URL}/core/support/sandbox/`;
export const SANDBOX_MAX_CHARS = 15000;

export const SAMPLE_COMPANY = "Northwind Electronics";
export const SAMPLE_KNOWLEDGE = `# Returns and refunds
You can return most items within 30 days of delivery for a full refund. Items must be unused and in the original packaging.
Refunds are paid back to the original payment method within 5 business days after we receive the item.
Opened software, gift cards and hygiene products cannot be returned.

# Shipping
Standard shipping takes 3 to 5 business days and is free on orders over 50 dollars. Express shipping takes 1 to 2 business days and costs 15 dollars.
We ship to the UK and the EU. We do not ship to other countries yet.
When an order ships, you receive an email with a tracking link.

# Warranty and repairs
All products have a 2 year warranty against manufacturing defects. Damage from drops or water is not covered.
To make a warranty claim, send your order number and a photo of the problem to support@northwind.example.

# Opening hours and contact
Our support team answers Monday to Friday, 9am to 6pm (UK time). Email: support@northwind.example. Phone: +44 20 7946 0000.
`;

export const SAMPLE_QUESTIONS = [
  "How long do I have to return something?",
  "Do you ship to Canada?",
  "My headphones stopped working after 8 months. What can I do?",
  "I want to talk to a real person",
];

// How many pieces of knowledge there are and how much of the limit is used: "# Title" lines make sections.
export function knowledgeStats(text) {
  const value = String(text || "");
  const sections = value.split("\n").filter((line) => /^\s*#{1,3}\s*\S/.test(line)).length;
  return { chars: value.length, sections, over: value.length > SANDBOX_MAX_CHARS };
}

export const PRIORITY_LABELS = { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" };
export const PRIORITY_STYLES = { low: "bg-gray-500/20 text-gray-200", normal: "bg-sky-500/20 text-sky-200", high: "bg-amber-500/20 text-amber-200", urgent: "bg-red-500/25 text-red-200" };

// What the ticket would look like in the inbox, built from the chat (the try-it box does not store anything).
export function ticketPreview({ messages, name, contact, priority }) {
  const questions = messages.filter((m) => m.role === "user").map((m) => m.text);
  const lines = messages.slice(-8).filter((m) => m.text).map((m) => `${m.role === "user" ? "Customer" : "Assistant"}: ${m.text}`);
  return {
    subject: (questions[0] || "Chat request").slice(0, 80),
    name: name || "(no name)",
    contact: contact || "",
    priority: priority || "normal",
    summary: lines.join("\n").slice(0, 600),
  };
}

// Asks the try-it box (a support assistant that knows only the visitor's text). onEvent gets { start }, { delta }, { done, reply, handoff, sources, priority }, { error: { message } }.
export async function askSandbox({ company, knowledge, message, history, onEvent, signal }) {
  let response;
  try {
    response = await fetch(SANDBOX_URL, { method: "POST", signal, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ company, knowledge, message, history }) });
  } catch (err) {
    if (err && err.name === "AbortError") throw err;
    onEvent({ error: { message: "Could not reach the server. Check your connection and try again." } });
    return;
  }
  if (!response.ok || !(response.headers.get("content-type") || "").includes("event-stream")) {
    let message2 = "Sorry, something went wrong. Please try again.";
    try { message2 = (await response.json()).error.message || message2; } catch { /* keep the general message */ }
    onEvent({ error: { message: message2 } });
    return;
  }
  await readEvents(response, onEvent);
}

// The conversation as the history the server wants: [{ role, content }] (answers that failed are left out).
export const historyOf = (messages) => messages.filter((m) => m.text && !m.error && !m.pending).map((m) => ({ role: m.role, content: m.text }));

// Ready-to-copy examples for the support endpoints.
export function supportSamples(base, key = "YOUR_API_KEY") {
  const api = `${base}/core/chatbot/v1`;
  return {
    flow: `# 1. The customer asks. When the answer has "handoff": true, a person is needed.
curl -X POST ${api}/chat/ \\
  -H "Authorization: Bearer ${key}" -H "Content-Type: application/json" \\
  -d '{"message": "My parcel never arrived and I want my money back"}'
# -> { "reply": "...", "handoff": true, "priority": "high", "sources": [], "message_id": 41, "conversation_id": "cv_..." }

# 2. Make the ticket (name and an email or a phone number are collected by your app)
curl -X POST ${api}/tickets/ \\
  -H "Authorization: Bearer ${key}" -H "Content-Type: application/json" \\
  -d '{"name": "Sara", "email": "sara@example.com", "conversation_id": "cv_...", "message": "Order 1234"}'
# -> { "reference": "T-00012", "status": "open", "priority": "high" }

# 3. Thumbs up or down for an answer
curl -X POST ${api}/feedback/ \\
  -H "Authorization: Bearer ${key}" -H "Content-Type: application/json" \\
  -d '{"message_id": 41, "helpful": false}'

# 4. Your own system reads the open tickets
curl "${api}/tickets/?status=open" -H "Authorization: Bearer ${key}"`,
  };
}
